import React, { useState, useEffect } from "react";
import { Pencil, Save, X, Loader2 } from "lucide-react";
import accessURL from "../../../components/utils/superAccess";

interface AdminDetails {
  id: number;
  name: string;
  email: string;
}

interface ApiResponse {
  code: number;
  data: AdminDetails;
}

const Details: React.FC = () => {
  const [details, setDetails] = useState<AdminDetails>({
    id: 0,
    name: "",
    email: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState<AdminDetails>({
    id: 0,
    name: "",
    email: "",
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await accessURL.get<ApiResponse>(
          "super-admin/getDetails"
        );

        if (response.data && response.data.data) {
          setDetails(response.data.data);
          setEditedDetails(response.data.data);
        } else {
          setError("Invalid data structure received from API");
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        setError("Failed to load admin details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = (): boolean => {
    const errors = {
      name: "",
      email: "",
    };
    let isValid = true;

    if (!editedDetails.name.trim()) {
      errors.name = "Name cannot be empty";
      isValid = false;
    }

    if (!editedDetails.email.trim()) {
      errors.email = "Email cannot be empty";
      isValid = false;
    } else if (!validateEmail(editedDetails.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const hasChanges = (): boolean => {
    return (
      details.name !== editedDetails.name ||
      details.email !== editedDetails.email
    );
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await accessURL.post("super-admin/updateDetails", editedDetails);
      setDetails(editedDetails);
      setIsEditing(false);
      setShowConfirmDialog(false);

      setTimeout(() => {
        window.location.href = "/administrator/login";
      }, 3000);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update details";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg flex justify-center items-center min-h-64">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="animate-spin" size={24} />
          <span>Loading admin details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
        <div className="text-red-500 mb-4 font-medium">{error}</div>
        <button
          onClick={() => {
            setError("");
            window.location.reload();
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">
            Super Admin Details
          </h2>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 text-gray-600 hover:text-blue-600 transition-colors rounded-full hover:bg-gray-100"
              aria-label="Edit details"
            >
              <Pencil size={20} />
            </button>
          ) : (
            <button
              onClick={() => {
                setIsEditing(false);
                setEditedDetails(details);
                setFormErrors({ name: "", email: "" });
              }}
              className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-full hover:bg-gray-100"
              aria-label="Cancel editing"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="name"
              >
                Name
              </label>
              {isEditing ? (
                <div>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={editedDetails.name}
                    onChange={handleInputChange}
                    className={`w-full p-2 border ${
                      formErrors.name ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow`}
                    required
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.name}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-lg text-gray-900">{details.name}</p>
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="email"
              >
                Email
              </label>
              {isEditing ? (
                <div>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={editedDetails.email}
                    onChange={handleInputChange}
                    className={`w-full p-2 border ${
                      formErrors.email ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow`}
                    required
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.email}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-lg text-gray-900">{details.email}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <button
              onClick={() =>
                hasChanges() && validateForm() && setShowConfirmDialog(true)
              }
              disabled={!hasChanges() || isSaving}
              className={`w-full py-2 px-4 rounded-md flex items-center justify-center gap-2 font-medium transition-colors ${
                hasChanges()
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              {isSaving ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <Save size={20} />
              )}
              Save Changes
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">
              Confirm Update
            </h3>
            <p className="text-gray-600">
              Are you sure you want to update the Super Admin details?
            </p>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                {isSaving && <Loader2 className="animate-spin" size={16} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;
